import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, MapPin, Users, Bed, ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK_IMAGES = [
  "/images/photo-1758548157747.jpg",
  "/images/photo-1760067537740.jpg",
  "/images/photo-1760067537116.jpg",
];

function getImages(logement, fallbackIndex) {
  if (logement.photos && logement.photos.length > 0) return logement.photos;
  return [FALLBACK_IMAGES[fallbackIndex % FALLBACK_IMAGES.length]];
}

export function PropertyCard({ logement, index = 0, variant = "default" }) {
  const [currentImg, setCurrentImg] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const images = getImages(logement, index);
  const hasMultiple = images.length > 1;

  const goTo = useCallback((e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImg((prev) => {
      if (dir === "next") return (prev + 1) % images.length;
      return (prev - 1 + images.length) % images.length;
    });
  }, [images.length]);

  const goToDot = useCallback((e, i) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImg(i);
  }, []);

  const isCompact = variant === "compact";

  return (
    <Link
      to={`/logement/${logement.id}`}
      className={`block group property-card ${logement.coup_de_coeur ? "property-card-featured" : ""}`}
      data-testid={`logement-card-${logement.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`${isCompact ? "aspect-[4/3]" : "aspect-[3/4]"} relative overflow-hidden`}>
        {/* Images with transition */}
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`${logement.titre} - ${i + 1}`}
            className={`card-image absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === currentImg ? "opacity-100" : "opacity-0"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}

        {/* Overlay */}
        <div className="card-overlay" />

        {/* Carousel Controls — visible on hover when multiple images */}
        {hasMultiple && isHovered && (
          <>
            <button
              onClick={(e) => goTo(e, "prev")}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white transition-all opacity-0 group-hover:opacity-100"
              data-testid={`carousel-prev-${logement.id}`}
            >
              <ChevronLeft className="w-4 h-4 text-[#2D2418]" />
            </button>
            <button
              onClick={(e) => goTo(e, "next")}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white transition-all opacity-0 group-hover:opacity-100"
              data-testid={`carousel-next-${logement.id}`}
            >
              <ChevronRight className="w-4 h-4 text-[#2D2418]" />
            </button>
          </>
        )}

        {/* Dots indicator */}
        {hasMultiple && (
          <div className="absolute bottom-14 left-0 right-0 flex justify-center gap-1.5 z-20">
            {images.slice(0, 5).map((_, i) => (
              <button
                key={i}
                onClick={(e) => goToDot(e, i)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === currentImg
                    ? "bg-white w-4"
                    : "bg-white/50 hover:bg-white/80"
                }`}
                data-testid={`carousel-dot-${logement.id}-${i}`}
              />
            ))}
            {images.length > 5 && (
              <span className="text-white/50 text-[8px] leading-none self-center">+{images.length - 5}</span>
            )}
          </div>
        )}

        {/* Top badges */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
          {logement.coup_de_coeur ? (
            <span className="bg-[#C9922A] text-white text-[10px] font-bold px-3 py-1.5 flex items-center gap-1.5 rounded-full shadow-[0_4px_16px_rgba(201,146,42,0.4)] uppercase tracking-widest">
              <Heart className="w-3 h-3 fill-white" />
              Coup de coeur
            </span>
          ) : (
            <span />
          )}
          <span className="bg-white/15 backdrop-blur-xl border border-white/20 px-3 py-1.5 rounded-full shadow-lg">
            <span className="font-mono font-bold text-white text-sm drop-shadow">
              {logement.prix_nuit} EUR
            </span>
            <span className="text-white/60 text-xs">/nuit</span>
          </span>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
          <h3 className="text-white font-semibold text-lg mb-1.5 drop-shadow-lg group-hover:text-[#E8C874] transition-colors duration-400 line-clamp-1">
            {logement.titre}
          </h3>
          <div className="flex items-center gap-3 text-white/70 text-sm mb-3">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-[#C9922A]" />
              {logement.ville}
            </span>
            {variant === "voyageurs" && (
              <>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {logement.capacite} pers.
                </span>
                <span className="flex items-center gap-1">
                  <Bed className="w-4 h-4" />
                  {logement.type_logement}
                </span>
              </>
            )}
          </div>

          {/* Details revealed on hover */}
          <div className="card-details">
            <div className="flex items-center justify-between bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-4 py-2.5">
              {variant === "voyageurs" ? (
                <span className="text-white/80 text-sm font-medium">Découvrir ce bien</span>
              ) : (
                <div className="flex items-center gap-3 text-white/80 text-xs">
                  <span className="flex items-center gap-1">
                    <Bed className="w-3.5 h-3.5" />
                    {logement.type_logement}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-[#C9922A]" />
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {logement.capacite} pers.
                  </span>
                </div>
              )}
              <div className="card-arrow w-7 h-7 bg-[#C9922A] rounded-full flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
